import { useState } from 'react';
import { Camera } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { Alert, Button, ModalOverlay } from './ui';

export async function uploadStudentPhoto(file: File) {
  const fd = new FormData();
  fd.append('photo', file);
  return api.post<{ hasPhoto: boolean; photoUrl: string | null }>('/api/auth/photo', fd);
}

/** Blocks booking / mentor ask until the student has a face photo. */
export function RequirePhotoModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { refetch } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const onPick = (next: File | null) => {
    setFile(next);
    setError('');
    if (preview) URL.revokeObjectURL(preview);
    setPreview(next ? URL.createObjectURL(next) : null);
  };

  const submit = async () => {
    if (!file) {
      setError('Choose a clear photo of your face.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await uploadStudentPhoto(file);
      await refetch();
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not save photo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onBackdropClick={e => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="require-photo-title"
        className="app-dialog bg-white shadow-2xl w-full max-w-sm p-5 sm:p-6"
      >
        <div className="flex items-center gap-2 mb-2">
          <Camera className="w-5 h-5 text-teal" />
          <h2 id="require-photo-title" className="font-heading text-lg font-bold text-ink">
            Profile photo
          </h2>
        </div>
        <p className="text-sm text-ink-soft mb-4">
          Clinics need to recognize who is coming. Add a clear photo of your face to continue.
        </p>
        {error && <Alert type="error" className="mb-3">{error}</Alert>}
        <label className="block mb-4">
          <span className="text-xs font-medium text-ink-soft">Photo</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="mt-1 block w-full text-sm text-ink-soft"
            onChange={e => onPick(e.target.files?.[0] ?? null)}
          />
        </label>
        {preview && (
          <img src={preview} alt="" className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border border-line" />
        )}
        <div className="flex gap-2">
          <Button variant="ghost" className="flex-1" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button className="flex-1" onClick={submit} loading={loading}>Save photo</Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
